import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils/cn';
import { Sidebar, useSidebarState } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';

// Authenticated layout: Sidebar (left) + TopBar (top) + page content.
// Owns the auth claims and provides them to the chrome — pages no longer
// need to render their own <Header>; layouts just wrap <Outlet/> in this.
//
// Pages are still authenticated by RequireAuth/RequireRole at the route
// level; this component just renders the chrome once auth is resolved.
export default function AppShell() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [role, setRole] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const sidebar = useSidebarState();

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setRole(null);
        setTenantId(null);
        return;
      }
      try {
        const token = await u.getIdTokenResult();
        setRole((token.claims.role as string | undefined) ?? null);
        setTenantId((token.claims.tenant_id as string | undefined) ?? null);
      } catch {
        setRole(null);
        setTenantId(null);
      }
    });
  }, []);

  // Until auth resolves, render a minimal scaffold so the page doesn't flash.
  // RequireAuth/RequireRole already gate access at the route level — by the
  // time we get here the user is authorised, but their token may still be
  // loading on first paint.
  if (user === undefined || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sage-700">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        role={role}
        collapsed={sidebar.collapsed}
        onToggleCollapse={sidebar.toggleCollapse}
        mobileOpen={sidebar.mobileOpen}
        onMobileClose={sidebar.closeMobile}
      />

      <div
        className={cn(
          'flex flex-col min-h-screen transition-all duration-200',
          sidebar.collapsed ? 'md:pl-16' : 'md:pl-60',
        )}
      >
        <TopBar
          userEmail={user.email ?? ''}
          userId={user.uid}
          tenantId={tenantId}
          onOpenMobileNav={sidebar.openMobile}
        />
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
